import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FileCode, Check, X, AlertCircle, ExternalLink, Copy } from 'lucide-react';
import { cn } from '@flowstate/utils';

interface EnhancedMarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * Enhanced Markdown Renderer with syntax highlighting and file path detection
 * Matches the Cascade chat interface style with rich formatting
 */
export const EnhancedMarkdownRenderer: React.FC<EnhancedMarkdownRendererProps> = ({ 
  content, 
  className 
}) => {
  // Detect file paths in text (e.g., apps/flowstate-automate/src/file.ts)
  const detectFilePath = (text: string): boolean => {
    const filePathPattern = /^[\w\-./]+\.(tsx?|jsx?|css|scss|json|md|html|py|java|go|rs|cpp|c|h|yml|yaml|toml|sql|sh|bat|ps1)$/i;
    return filePathPattern.test(text.trim());
  };

  // Extract language from className (e.g., "language-typescript" -> "typescript")
  const getLanguage = (className: string | undefined): string => {
    const match = /language-(\w+)/.exec(className || '');
    return match ? match[1] : 'text';
  };

  // Copy code block handler
  const [copiedBlock, setCopiedBlock] = useState<string | null>(null);
  const handleCopyCode = useCallback((code: string, id: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedBlock(id);
      setTimeout(() => setCopiedBlock(null), 2000);
    });
  }, []);

  return (
    <div className={cn(
      "prose prose-base max-w-none dark:prose-invert enhanced-markdown",
      "prose-p:text-foreground/90 prose-li:text-foreground/90",
      "prose-headings:text-foreground",
      className
    )}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Code blocks with syntax highlighting and copy button
          code: ({ className, children, ...props }: any) => {
            const codeContent = String(children).replace(/\n$/, '');
            const language = getLanguage(className);
            const isInline = !className || !className.startsWith('language-');
            const blockId = `code-${codeContent.slice(0, 20)}`;

            if (!isInline) {
              return (
                <div className="my-4 rounded-lg overflow-hidden border border-border/40 bg-[#1e1e1e] shadow-sm">
                  <div className="flex items-center justify-between px-4 py-2 bg-[#2d2d2d] border-b border-border/20">
                    <span className="text-xs font-mono text-zinc-400 uppercase tracking-wide">
                      {language}
                    </span>
                    <button
                      onClick={() => handleCopyCode(codeContent, blockId)}
                      className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200 transition-colors px-2 py-1 rounded hover:bg-white/5"
                    >
                      {copiedBlock === blockId ? (
                        <><Check className="h-3 w-3 text-green-400" /> Copied</>
                      ) : (
                        <><Copy className="h-3 w-3" /> Copy</>
                      )}
                    </button>
                  </div>
                  <SyntaxHighlighter
                    language={language}
                    style={vscDarkPlus}
                    customStyle={{
                      margin: '0',
                      padding: '1rem',
                      background: '#1e1e1e',
                      fontSize: '0.8125rem',
                      lineHeight: '1.6',
                    } as any}
                    showLineNumbers={codeContent.split('\n').length > 3}
                    wrapLines={true}
                    wrapLongLines={true}
                  >
                    {codeContent}
                  </SyntaxHighlighter>
                </div>
              );
            }

            // Inline code - high contrast
            return (
              <code 
                className="bg-primary/10 text-primary dark:bg-primary/15 dark:text-blue-300 px-1.5 py-0.5 rounded text-[0.8125rem] font-mono font-medium"
                {...props}
              >
                {children}
              </code>
            );
          },

          // Links - highly visible, clickable with external icon
          a: ({ href, children, ...props }) => {
            const text = String(children);
            const isFilePath = detectFilePath(text);
            const isExternal = href?.startsWith('http');

            if (isFilePath) {
              return (
                <a
                  href={href}
                  className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 underline decoration-blue-400/40 hover:decoration-blue-300 underline-offset-2 font-mono text-sm transition-colors"
                  {...props}
                >
                  <FileCode className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{text}</span>
                </a>
              );
            }

            return (
              <a
                href={href}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
                className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 underline decoration-blue-400/40 hover:decoration-blue-300 underline-offset-2 transition-colors break-words"
                {...props}
              >
                {children}
                {isExternal && <ExternalLink className="h-3 w-3 flex-shrink-0 inline" />}
              </a>
            );
          },

          // Paragraphs - comfortable reading line height
          p: ({ children, ...props }) => (
            <p className="leading-7 my-2.5 text-foreground/90" {...props}>
              {children}
            </p>
          ),

          // Headings - clear visual hierarchy
          h1: ({ children, ...props }) => (
            <h1 className="text-xl font-bold mt-6 mb-3 text-foreground border-b border-border/40 pb-2" {...props}>
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 className="text-lg font-bold mt-5 mb-2.5 text-foreground" {...props}>
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 className="text-base font-bold mt-4 mb-2 text-foreground" {...props}>
              {children}
            </h3>
          ),
          h4: ({ children, ...props }) => (
            <h4 className="text-sm font-bold mt-3 mb-1.5 text-foreground" {...props}>
              {children}
            </h4>
          ),

          // Lists - clean spacing, proper indentation
          ul: ({ children, ...props }) => (
            <ul className="list-disc list-outside ml-5 space-y-1.5 my-2.5" {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="list-decimal list-outside ml-5 space-y-1.5 my-2.5" {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => {
            const text = String(children);
            
            // Detect checkmarks and status indicators
            if (text.startsWith('✅') || text.includes('✓')) {
              return (
                <li className="flex items-start gap-2 marker:text-transparent" {...props}>
                  <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-foreground/90">{text.replace(/^(✅|✓)\s*/, '')}</span>
                </li>
              );
            }
            
            if (text.startsWith('❌') || text.startsWith('✗')) {
              return (
                <li className="flex items-start gap-2 marker:text-transparent" {...props}>
                  <X className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <span className="text-foreground/90">{text.replace(/^(❌|✗)\s*/, '')}</span>
                </li>
              );
            }

            if (text.startsWith('⚠️') || text.startsWith('⏳')) {
              return (
                <li className="flex items-start gap-2 marker:text-transparent" {...props}>
                  <AlertCircle className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <span className="text-foreground/90">{text.replace(/^(⚠️|⏳)\s*/, '')}</span>
                </li>
              );
            }

            return <li className="text-foreground/90 leading-7" {...props}>{children}</li>;
          },

          // Strong/bold text - high contrast
          strong: ({ children, ...props }) => (
            <strong className="font-bold text-foreground" {...props}>
              {children}
            </strong>
          ),

          // Emphasis/italic text - readable contrast
          em: ({ children, ...props }) => (
            <em className="italic text-foreground/80" {...props}>
              {children}
            </em>
          ),

          // Blockquotes - readable with clear visual distinction
          blockquote: ({ children, ...props }) => (
            <blockquote 
              className="border-l-4 border-blue-500/60 pl-4 py-1 my-4 bg-blue-500/5 rounded-r-md [&>p]:text-foreground/80 [&>p]:my-1"
              {...props}
            >
              {children}
            </blockquote>
          ),

          // Horizontal rules
          hr: ({ ...props }) => (
            <hr className="my-6 border-border/40" {...props} />
          ),

          // Tables - clean with alternating rows
          table: ({ children, ...props }) => (
            <div className="my-4 overflow-x-auto rounded-lg border border-border/40">
              <table className="min-w-full border-collapse" {...props}>
                {children}
              </table>
            </div>
          ),
          thead: ({ children, ...props }) => (
            <thead className="bg-muted/60" {...props}>
              {children}
            </thead>
          ),
          th: ({ children, ...props }) => (
            <th className="border-b border-border/40 px-4 py-2.5 text-left text-sm font-bold text-foreground" {...props}>
              {children}
            </th>
          ),
          tr: ({ children, ...props }) => (
            <tr className="even:bg-muted/20 hover:bg-muted/30 transition-colors" {...props}>
              {children}
            </tr>
          ),
          td: ({ children, ...props }) => (
            <td className="border-b border-border/20 px-4 py-2.5 text-sm" {...props}>
              {children}
            </td>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
